const en = {
    // ==================== COMMON ====================
    common: {
        appName: 'Email Lion',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        save: 'Save',
        cancel: 'Cancel',
        close: 'Close',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        update: 'Update',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        search: 'Search',
        refresh: 'Refresh',
        send: 'Send',
        confirm: 'Confirm',
        yes: 'Yes',
        no: 'No',
        or: 'or',
        all: 'All',
        none: 'None',
        status: 'Status',
        name: 'Name',
        actions: 'Actions',
        date: 'Date',
        time: 'Time',
        today: 'Today',
        print: 'Print',
        export: 'Export',
        import: 'Import',
        test: 'Test',
        enabled: 'Enabled',
        disabled: 'Disabled',
        default: 'Default',
        optional: 'Optional',
        required: 'Required',
        noData: 'No data to display',
        errorLoadingData: 'Error loading data',
        errorSaving: 'Error saving',
        errorDeleting: 'Error deleting',
        voiceBlocked: 'The browser blocked the microphone. Allow access in site settings and try again.',
        voiceNeedReload: 'The microphone is already allowed in site settings, but this page has not picked it up yet. Reload, then tap the microphone again.',
        voiceNoDevice: 'No microphone was found. Check that a device is connected and allowed in Windows or macOS.',
        voiceBusy: 'The microphone is in use by another app. Close Zoom, Teams or Meet and try again.',
        voiceSpeechFailed: 'Speech recognition failed. Try again, or reload the page.',
        voiceNotSupported: 'Voice input is not supported in this browser. Use Chrome.',
        voiceInputTooltip: 'Voice input',
        voiceStop: 'Stop recording',
        voiceListening: 'Listening... speak now',
        upgradeRequired: 'To use this module, please upgrade your plan or contact your system administrator.',
        viewPlans: 'View Plans & Upgrade',
        files: 'Files',
        from: 'From',
        subject: 'Subject',
        noSubject: '(No subject)',
        email: 'Email',
        password: 'Password',
        emailAccount: 'Email Account',
        runNow: 'Run Now',
        description: 'Description',
        errorLoadingAccounts: 'Error loading accounts',
        errorStatusChange: 'Error changing status',
        errorLogin: 'Login error',
        use: 'Use',
        recommended: 'Recommended',
        free: 'Free',
    },

    // ==================== LOGIN ====================
    login: {
        subtitle: 'Enterprise Email System',
        loginTab: 'Login',
        registerTab: 'Free Sign Up',
        emailLabel: 'Email',
        passwordLabel: 'Password',
        twoFactorLabel: 'Two-Factor Authentication Code (2FA)',
        twoFactorHelper: 'Enter the code from your authenticator app',
        verifyAndLogin: 'Verify & Login',
        loginButton: 'Login',
        forgotPassword: 'Forgot Password',
        fullName: 'Full Name *',
        emailRequired: 'Email *',
        passwordRequired: 'Password *',
        minChars: 'At least 6 characters',
        confirmPassword: 'Confirm Password *',
        passwordsMismatch: 'Passwords do not match',
        companyName: 'Company Name (optional)',
        phone: 'Phone *',
        phoneHelper: 'Valid mobile or landline (at least 9 digits)',
        registering: 'Signing up...',
        registerFree: 'Sign Up Free',
        freePlan: 'Free plan · No credit card · 1 mailbox',
        googleLogin: 'Login with Google',
        googleRegister: 'Sign Up with Google',
        termsAgree: 'By signing up, I agree to the',
        termsOfService: 'Terms of Service',
        privacyPolicy: 'Privacy Policy',
        backToSite: 'Back to Homepage',
        resetPassword: 'Reset Password',
        stepEnterEmail: 'Enter Email',
        stepVerifyCode: 'Verify Code',
        stepNewPassword: 'New Password',
        resetEmailPrompt: 'Enter your email address and we will send you a password reset code.',
        emailAddress: 'Email Address',
        codeSentTo: 'A verification code was sent to ',
        enterCode: '. Enter the code:',
        verificationCode: 'Verification Code (6 digits)',
        newPasswordPrompt: 'Enter your new password (at least 6 characters):',
        newPassword: 'New Password',
        confirmPasswordLabel: 'Confirm Password',
        sendCode: 'Send Code',
        verifyCode: 'Verify Code',
        resetPasswordBtn: 'Reset Password',
        sessionExpired: 'Session expired. Please login again.',
        errorFullName: 'Please enter your full name',
        errorValidEmail: 'Please enter a valid email address (not a temporary one)',
        errorPhone: 'Please enter a valid phone number (at least 9 digits)',
        errorMinPassword: 'Password must be at least 6 characters',
        errorPasswordMatch: 'Passwords do not match',
        registerSuccess: 'Registration complete! You can now login.',
        errorRegister: 'Registration error',
        errorGoogleAuth: 'Google authentication error',
        errorGoogleFailed: 'Google authentication failed, please try again.',
        errorGoogleUnavailable: 'Google service is currently unavailable',
        errorEnterEmail: 'Please enter your email address',
        errorSendCode: 'Error sending code',
        errorEnterCode: 'Please enter a 6-digit code',
        errorResetPassword: 'Error resetting password',
        and: 'and the',
        rememberMe30: 'Remember me for 30 days',
        keepLoggedInForever: 'Keep me logged in on this device',
        keepLoggedInHint: 'Recommended for a personal mobile phone. Do not use on a shared computer.',
    },

    // ==================== LAYOUT ====================
    layout: {
        dashboard: 'Dashboard',
        webmail: 'Webmail',
        calendar: 'Calendar',
        orgManagement: 'Organization',
        organizations: 'Organizations',
        domains: 'Domains',
        mailboxes: 'Mailboxes',
        users: 'Users',
        operations: 'Billing & Ops',
        plans: 'Plans & Storage',
        proposals: 'Proposals',
        billing: 'Billing',
        branding: 'Branding',
        monitoring: 'Security',
        reports: 'Reports & Logs',
        systemHealth: 'System Health',
        auditLog: 'Audit Log',
        tools: 'Tools',
        aiAgent: 'Personal AI Agent',
        automations: 'AI Automations',
        filing: 'Document Filing',
        smartCleanup: 'AI Smart Cleanup',
        autoReplyLog: 'AI Reply Log',
        migration: 'Email Migration',
        marketingCockpit: 'Marketing',
        salesEngine: 'Sales Engine',
        integrations: 'Integrations',
        settings: 'Settings',
        viewingAsUser: 'Viewing as User',
        backToAdmin: 'Back to Admin',
        viewingAsUserClick: 'Viewing as User – Click to Return',
        website: 'Website',
        backToWebsite: 'Back to Website',
        help: 'Help',
        helpCenter: 'Help Center',
        helpTooltip: 'Help & Tutorials',
        darkMode: 'Dark Mode',
        lightMode: 'Light Mode',
        expandMenu: 'Expand Menu',
        collapseMenu: 'Collapse Menu',
        logout: 'Logout',
        language: 'Language',
        roles: {
            super_admin: 'Super Admin',
            multi_org_admin: 'Multi-Org Admin',
            org_admin: 'Org Admin',
            user: 'User',
        },
    },

    // ==================== SMART CLEANUP ====================
    cleanup: {
        title: 'AI Smart Cleanup Agent',
        subtitle: 'Smart scan of your mailbox: identify newsletters, spam, old emails, large files, and duplicates. Unsubscribe, delete, move to folders, and clean up smartly.',
        startScan: 'Start Scan',
        scanning: 'Scanning...',
        errorScan: 'Error starting scan',
        errorScanResult: 'Scan error',
        noAccountTitle: 'Connect an email account to use the cleanup agent',
        noAccountSubtitle: 'Go to Webmail and connect a mailbox first',
        readyTitle: 'Ready to clean up your mailbox?',
        readySubtitle: 'The smart agent will scan your inbox, find newsletters, spam, old and large emails, and suggest smart cleanup actions. Choose what to delete, move, or archive.',
        startSmartScan: 'Start Smart Scan',
        newsletters: 'Newsletters',
        newslettersDesc: 'Emails with unsubscribe option',
        spam: 'Spam',
        spamDesc: 'Suspected spam emails',
        old: 'Old (6+ months)',
        oldDesc: 'Emails older than 6 months',
        large: 'Large (5MB+)',
        largeDesc: 'Emails with large attachments',
        notifications: 'Notifications',
        notificationsDesc: 'Automated notifications',
        duplicates: 'Duplicates',
        duplicatesDesc: 'Emails with identical subjects',
        deselectAll: 'Deselect All',
        selectAll: 'Select All',
        selected: 'selected',
        deleteSelected: 'Delete Selected',
        deleteBtn: 'Delete',
        moveToFolder: 'Move to Folder',
        moveBtn: 'Move',
        archive: 'Archive',
        markSpam: 'Mark as Spam',
        spamBtn: 'Spam',
        markRead: 'Mark as Read',
        readBtn: 'Read',
        page: 'Page',
        noneFound: 'No emails found in this category',
        unsubscribe: 'Unsub',
        unsubscribeTooltip: 'Unsubscribe from mailing list',
        deleteConfirmTitle: 'Confirm Delete',
        deleteConfirmMsg: 'This will delete {count} emails. Deleted emails cannot be recovered.',
        deleteCount: 'Delete {count} emails',
        moveTitle: 'Move to Folder',
        movePrompt: 'Choose a target folder for {count} emails:',
        folderName: 'Folder Name',
        folderPlaceholder: 'e.g.: Newsletters, Clients, Archive...',
        folderHelper: 'Enter an existing or new folder name',
        executedResult: 'Done: {success} succeeded, {failed} failed',
        errorExecute: 'Error executing action',
        rescan: 'Rescan',
        loadingCached: 'Loading previous scan results...',
        cachedLabel: 'Cached',
        lastScanAt: 'Last scan: {date}',
        insightsSummary: 'Scanned {total} emails · Found {items} items to handle',
        organizeBtn: 'Organize to Folders',
        organizeTooltip: 'Automatically move newsletters, notifications and spam to organized folders',
        organizeTitle: 'Auto-Organize to Folders',
        organizeDesc: 'The smart agent will automatically move emails to organized folders based on the scan classification:',
        organizeInfo: 'Folders that don\'t exist will be created automatically. Moved emails will leave the INBOX.',
        organizeComplete: 'Organization completed successfully!',
        organizeSuccess: 'INBOX organized to folders successfully',
        organizing: 'Organizing...',
        organizeNow: 'Organize Now',
        errorOrganize: 'Error organizing to folders',
        movedToFolder: 'moved to folder',
        failedItems: 'Failed',
        foldersCreated: 'Folders created',
    },

    // ==================== CALENDAR ====================
    calendar: {
        days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        newEvent: 'New Event',
        editEvent: 'Edit Event',
        title: 'Title',
        descriptionLabel: 'Description',
        location: 'Location',
        start: 'Start',
        end: 'End',
        allDay: 'All Day',
        color: 'Color:',
        month: 'Month',
        week: 'Week',
        day: 'Day',
        addEvent: 'Add Event',
        eventCreated: 'Event created successfully',
        eventUpdated: 'Event updated',
        eventDeleted: 'Event deleted',
        eventMoved: 'Event moved',
        undo: 'Undo',
        redo: 'Redo',
        undoDone: 'Undone',
        redoDone: 'Redone',
        undoFailed: 'Could not undo',
        redoFailed: 'Could not redo',
        deleteEvent: 'Delete this event?',
        errorSaving: 'Error saving',
        errorDeleting: 'Error deleting',
        requiredFields: 'Title, start date and end date are required',
        noReminder: 'No reminder',
        min5: '5 minutes before',
        min10: '10 minutes before',
        min15: '15 minutes before',
        min30: '30 minutes before',
        hour1: '1 hour before',
        hour2: '2 hours before',
        day1: '1 day before',
        once: 'One-time',
        daily: 'Daily',
        weekly: 'Weekly',
        monthly: 'Monthly',
        yearly: 'Yearly',
        printCalendar: 'Print Calendar',
        printPrompt: 'Choose a print view:',
        dailyView: 'Daily',
        weeklyView: 'Weekly',
        monthlyView: 'Monthly',
        yearlyView: 'Yearly',
        yearLabel: 'Year',
        weekLabel: 'Week',
        printWindowBlocked: 'Print window was blocked – allow popups',
        syncCalendar: 'Calendar Sync',
        syncTooltip: 'Google / Outlook / iCal sync',
        subscriptionUrl: 'Subscription URL (iCal)',
        subscriptionHelper: 'Copy the URL and add it as an external calendar (one-way outbound). Do not add it to Google if OAuth is already connected.',
        copy: 'Copy',
        regenerate: 'New',
        syncUrlCopied: 'Sync URL copied',
        syncUrlRegenerated: 'New sync URL generated',
        exportIcs: 'Export ICS',
        importIcs: 'Import ICS',
        exportDone: 'File downloaded',
        importDone: '{count} events imported',
        errorExport: 'Error exporting',
        errorImport: 'Error importing',
        exportImport: 'Export / Import',
        externalSyncTitle: 'Automatic two-way sync',
        externalSyncHelper: 'Connect Google Calendar or Outlook — events appear here and sync both ways automatically.',
        connectGoogle: 'Connect Google Calendar',
        connectOutlook: 'Connect Outlook',
        disconnectGoogle: 'Disconnect Google',
        disconnectOutlook: 'Disconnect Outlook',
        syncNow: 'Sync now',
        syncDone: 'Sync completed',
        syncFailed: 'Sync failed',
        connectedAs: 'Connected as {email}',
        lastSync: 'Last sync: {time}',
        sourceGoogle: 'Google',
        sourceOutlook: 'Outlook',
        sourceLocal: 'Local',
        notConfiguredGoogle: 'Google Calendar OAuth is not configured on the server',
        notConfiguredOutlook: 'Outlook Calendar OAuth is not configured on the server',
        calendarHint: 'Email Lion - Calendar',
        aiAgent: 'AI Calendar Agent',
        aiAgentTooltip: 'AI Smart Agent',
        aiHello: 'Hi! I\'m your calendar agent',
        aiSubtitle: 'Tell me what to schedule in plain language',
        aiPlaceholder: 'Tell me what to schedule and I\'ll handle it',
        aiInputPlaceholder: 'e.g.: "Team meeting Thursday at 3:00 PM"',
        aiDaySummary: 'Day Summary',
        aiThinking: 'Thinking...',
        voiceInputTooltip: 'Voice input',
        voiceStop: 'Stop recording',
        voiceListening: 'Listening... speak now',
        voiceNotSupported: 'Voice input is not supported in this browser',
        aiErrorSummary: 'Error getting day summary',
        requestProcessed: 'Request processed successfully.',
        eventsCreated: '{count} events created:',
        more: 'more',
        reminder: 'Reminder',
        reminderSnack: '🔔 Reminder: {title} at {time}',
        reminderBody: 'At {time}',
        aiCreatedEvents: '📅 {count} events created:',
        aiNoResponse: 'No response received.',
        aiSummaryPrefix: '📊 Summary for {date}:',
        aiNoInfo: 'No information',
        aiExampleChip: 'Meeting tomorrow at 10',
        aiExampleChipPrompt: 'Schedule a meeting tomorrow at ten AM in Tel Aviv with the supplier',
        aiExample1: 'Schedule a meeting tomorrow at ten AM in Tel Aviv',
        aiExample2: 'Create 3 weekly reminders: review, team, and summary',
        aiExample3: 'Urgent board meeting today at 4:00 PM + reminder',
        syncCopyTooltip: 'Copy',
        syncRegenerateTooltip: 'Regenerate',
        syncGoogleInstructions: 'Settings - Add calendar - From URL',
        syncAppleInstructions: 'File - New subscription',
        syncOutlookInstructions: 'Add calendar - Subscribe from web',
        icalGoogleWarning: 'Google Calendar is already connected with two-way sync. Do not add the iCal URL to Google — that duplicates every event. Uncheck "Email Lion Calendar" in Google Calendar\'s left sidebar. Use the iCal URL only for Apple Calendar or another calendar.',
        dedupeButton: 'Remove duplicates',
        dedupeConfirm: 'Delete duplicate rows in Email Lion (the same event twice)? Events in Google itself will not be deleted — if duplicates appear there, uncheck "Email Lion Calendar" in the left sidebar.',
        dedupeDone: 'Removed {count} duplicates',
        dedupeNone: 'No duplicates found in Email Lion',
        aiClearChat: 'Clear chat',
        aiClearConfirm: 'Clear the conversation history with the agent?',
    },

    // ==================== AUTOMATIONS ====================
    automations: {
        title: 'AI Email Automations',
        subtitle: 'Create automatic rules to process emails with AI – data extraction, summaries, calculations, and more',
        moduleTitle: 'AI Email Automations',
        moduleDesc: 'Work smarter – set up rules that automatically process emails and attachments with AI. Extract data from PDFs, summarize documents, calculate spreadsheets, and more.',
        moduleFeatures: ['PDF/Excel/CSV Processing', 'Document Summaries', 'Data Extraction', 'Email/Webhook Output', '3 AI Engines'],
        newAutomation: 'New Automation',
        editAutomation: 'Edit Automation',
        runAllNow: 'Run Now',
        runAllTooltip: 'Run all automations now',
        runningInBackground: 'Automations are running in the background...',
        errorRunning: 'Error running',
        totalAutomations: 'Total Automations',
        active: 'Active',
        runs30days: 'Runs (30 days)',
        successRate: 'Success Rate',
        tabMy: 'My Automations',
        tabHistory: 'Run History',
        tabTemplates: 'Templates',
        noAutomations: 'No automations yet. Create your first one or choose a template!',
        noRuns: 'No runs yet',
        filters: 'Filters',
        output: 'Output',
        runsCount: 'Runs',
        lastRun: 'Last Run',
        outputEmail: 'Send by Email',
        outputWebhook: 'Webhook (Zapier/Make)',
        outputDownload: 'Download as PDF',
        outputEmailWebhook: 'Email + Webhook',
        formatText: 'Free Text',
        formatJson: 'Structured JSON',
        formatTable: 'Table',
        formatSummary: 'Bullet Summary',
        statusSuccess: 'Success',
        statusFailed: 'Failed',
        statusProcessing: 'Processing...',
        statusSkipped: 'Skipped',
        automationUpdated: 'Automation updated',
        automationCreated: 'Automation created',
        automationDeleted: 'Automation deleted',
        deleteConfirm: 'Delete this automation?',
        testCompleted: 'Test completed successfully',
        testTimeout: 'The test took too long. Try again.',
        testConnectionLost: 'Server connection lost. The server may be restarting – try again in 30 seconds.',
        testFailed: 'Test failed – check AI settings and mailbox',
        downloadPdfError: 'Error downloading PDF',
        useTemplate: 'Use Template',
        downloadPdf: 'Download as PDF',
        stepTrigger: 'Trigger & Filters',
        stepAi: 'AI Processing',
        stepOutput: 'Output & Delivery',
        automationName: 'Automation Name',
        descriptionOptional: 'Description (optional)',
        filterFrom: 'From (address or part of it, supports /regex/)',
        filterFromHelper: 'Separate multiple addresses with commas. Example: accounting@, billing@company.com or /.*@company\\.co\\.il/',
        filterSubject: 'Subject contains (comma-separated keywords)',
        filterSubjectHelper: 'Example: invoice,receipt,order',
        filterBody: 'Body contains (keywords)',
        blockedSenders: 'Blocked senders',
        blockedSendersHelper: 'Email addresses or domains to block, comma-separated. Example: spam@example.com, noreply@domain.com',
        onlyWithAttachment: 'Only emails with attachments',
        fileType: 'File Type',
        fileTypeAll: 'All',
        fileTypeImage: 'Image',
        fileTypeAny: 'Any type',
        aiEngine: 'AI Engine',
        aiDefault: 'Default (from settings)',
        aiOpenai: 'OpenAI (GPT-4o) – Best for Excel',
        aiAnthropic: 'Anthropic (Claude) – Best for PDF',
        aiGoogle: 'Google (Gemini) – Fast & efficient',
        outputFormat: 'Output Format',
        aiPrompt: 'AI Prompt (processing instructions)',
        aiPromptHelper: 'You can use variables: {email_body}, {attachment_content}, {sender}, {subject}, {date}',
        includeBody: 'Include email body',
        includeAttachments: 'Include attachments',
        aiTips: 'Tips:',
        aiTipsText: 'Claude excels at reading complex PDFs. GPT-4o excels at analyzing tables and numbers. Gemini is fast and efficient for large processing.',
        outputType: 'Output Type',
        sendTarget: 'Send Target',
        sendToSelf: 'My Account (default)',
        sendToSender: 'Reply to sender',
        sendToCustom: 'Custom address',
        customEmail: 'Custom Email Address',
        customEmailHelper: 'Example: ops@company.com',
        emailSubjectTemplate: 'Email Subject Template',
        emailSubjectHelper: 'You can use: {subject}, {automation_name}',
        attachAsPdf: 'Attach result as PDF',
        webhookHeaders: 'Webhook Headers (JSON, optional)',
        webhookInfo: 'The Webhook will send JSON with fields: automation_name, trigger (from, subject, date), result (text, format). If the output format is JSON, the result.parsed field will contain the parsed JSON.',
        limits: 'Limits',
        maxRunsPerDay: 'Max runs per day',
        activeHoursStart: 'Start Hour',
        activeHoursEnd: 'End Hour',
        enableImmediately: 'Enable automation immediately after saving',
        testResultTitle: 'Test Result',
        aiResult: 'AI Result:',
        sentTo: 'Sent to:',
        inputSentToAi: 'Input sent to AI:',
        processingTime: 'Processing time:',
        testedWithReal: 'Tested with a real email',
        testedWithMock: 'Tested with a mock email (no real email found in inbox)',
        createAutomation: 'Create Automation',
        fromFilter: 'From:',
        subjectFilter: 'Subject:',
        attachment: 'Attachment',
        // Multi-output (filing / cloud)
        additionalOutputs: 'Additional outputs',
        fileToCloud: 'Also file to Google Drive / Dropbox / OneDrive',
        filingConnection: 'Filing connection',
        filingFolderPath: 'Target folder path (optional)',
        filingFolderHelper: 'Separated by "/". Supports variables: {date}, {subject}, {from_name}, {automation_name} and JSON fields from the AI output.',
        fileAttachments: 'File attachments',
        fileEmailBody: 'File the email body',
        fileAiResult: 'File the AI result',
        noFilingConnections: 'No active Google Drive / Dropbox / OneDrive connection. Add one in the "Filing" page to enable this option.',
        filingNeedsConnection: 'A Drive/Dropbox/OneDrive connection is required to enable filing as an output.',
        emailBodyTemplate: 'Email body template (optional)',
        emailBodyHelper: 'If empty, the email body will be the raw AI output. Supports the same variables as the subject line.',
        templateVarsHelper: 'Available variables: {subject}, {from_name}, {from}, {date}, {date:dd/MM}, {ai_result} (also {ai_text}). If the AI output is JSON, also {{vendor}}, {{amount}}, {ai_json.amount} etc.',
        // Workflow output targets
        createTaskOption: 'Create automation task (Task Manager)',
        generateReplyDraftOption: 'Generate reply draft for approval (Smart Reply)',
        sendAlertOption: 'Send urgent alert (Alert)',
        taskDefaultStatus: 'Default status',
        taskDefaultPriority: 'Priority',
        taskDefaultTags: 'Tags (comma-separated)',
        taskStatusOpen: 'Open',
        taskStatusUrgent: 'Urgent',
        taskStatusWaitingSupplier: 'Waiting for supplier',
        taskStatusWaitingCustomer: 'Waiting for customer',
        taskPriorityLow: 'Low',
        taskPriorityMedium: 'Medium',
        taskPriorityHigh: 'High',
        taskPriorityUrgent: 'Urgent',
        taskJsonHint: 'Tip: to let the AI dynamically set title/status/priority, use ai_output_format=json and ask the prompt to return { title, summary, status, priority, tags, followup_hours }.',
        replyDraftDefaultTo: 'Default recipient',
        replyDraftDefaultToHelper: 'Keep "__sender__" to reply to the original sender, or enter a fixed email.',
        alertRecipientsLabel: 'Alert recipients (comma-separated)',
        alertRecipientsHelper: 'If empty, the alert is sent to the automation account. A push notification is also sent to the user.',
        // Trigger types
        triggerSection: 'Trigger',
        triggerType: 'Trigger type',
        triggerEmail: 'Incoming email (default)',
        triggerSchedule: 'Scheduled (daily digest / report)',
        triggerFollowup: 'Automatic follow-up on open tasks',
        scheduleTime: 'Schedule time',
        scheduleDays: 'Active days',
        scheduleDaysHelper: '0=Sun, 1=Mon, ..., 6=Sat',
        followupWaitHours: 'Hours to wait before follow-up',
        followupWaitHelper: 'The automation will trigger a follow-up on tasks that have not been updated within this window.',
        scheduleTimeInvalid: 'Schedule time must be in HH:MM format',
        followupWaitInvalid: 'For a Follow-Up trigger, enter a positive number of hours',
        // Reference materials (knowledge base files attached to the prompt)
        referenceMaterials: 'Reference materials for this automation',
        referenceMaterialsHelper: 'Reference files or URLs that will be attached on every run. Useful for minimum-order tables, price lists, customer lists, etc. Public Google Sheets are auto-downloaded as CSV.',
        addReferenceMaterial: 'Add reference material',
        referenceMaterialName: 'Descriptive name',
        referenceMaterialUrl: 'URL (Google Sheet / Drive / direct link)',
        referenceMaterialUrlHelper: 'A public Google Sheet is auto-downloaded as CSV. For a private Drive file, share it with "anyone with the link".',
        referenceMaterialRemove: 'Remove',
        // Task manager
        tasksTitle: 'Open Tasks',
        taskUpdated: 'Task updated',
        taskUpdateError: 'Error updating task',
        taskDeleteConfirm: 'Delete this task? This action cannot be undone.',
        taskDeleted: 'Task deleted',
        taskDeleteError: 'Error deleting',
        taskFilterOpenOnly: 'Open only',
        taskFilterUrgent: 'Urgent',
        taskFilterOpen: 'Open',
        taskFilterWaitingSupplier: 'Waiting for supplier',
        taskFilterWaitingCustomer: 'Waiting for customer',
        taskFilterClosed: 'Closed',
        taskFilterAll: 'All',
        tasksCount: '{count} tasks',
        taskColTitle: 'Title / Summary',
        taskColSource: 'Source',
        taskColAutomation: 'Automation',
        taskColLastAction: 'Last action',
        noOpenTasks: 'No open tasks right now',
        tasksAutoInfo: 'Tasks created automatically from emails will appear here.',
        taskStatusClosed: 'Closed',
        taskPrioUrgent: 'Urgent',
        taskPrioHigh: 'High',
        taskPrioMedium: 'Medium',
        taskPrioLow: 'Low',
        taskReopen: 'Reopen',
        taskCloseTask: 'Close task',
        taskShowMore: 'Show more',
        taskShowLess: 'Show less',
        taskViewFull: 'Full details',
        taskSummaryFull: 'Full summary',
        taskNoSummary: 'No summary for this task',
        taskStatusStuck: 'Stuck',
        refNamePlaceholder: 'Minimum order table',
        emailBodyPlaceholder: 'Example: Hello,\nNew invoice from {from_name}.\nSupplier: {{supplier}}, Amount: {{amount}}.\n\n{ai_result}',
        filingPathPlaceholder: '{date}/Automations/{from_name}',
        refMaterialsLoadedSummary: 'Reference materials: {ok}/{total} loaded',
        extractWarningPrefix: 'Error loading email content / files:',
        refMaterialsLoadedTitle: 'Loaded reference materials ({ok}/{total})',
        downloadedFile: 'Downloaded file:',
        refMaterialChip: 'Reference material',
    },

    // ==================== FILING ====================
    filing: {
        title: 'Smart Document Filing',
        subtitle: 'Automatically file email attachments to cloud with AI',
        skippedNoDocument: 'No downloadable document found at link',
        retryFailedBtn: 'Retry (failed)',
        retryFailedSuccess: 'Failed jobs were queued for retry',
        retryFailedError: 'Error resetting jobs',
        moduleDesc: 'File email attachments directly to Google Drive, Dropbox, or OneDrive – automatically with AI. The system identifies invoices, orders, contracts, and more, and files them in organized folders.',
        moduleFeatures: ['Google Drive', 'Dropbox', 'OneDrive', 'AI Document Recognition', 'Automatic Folders', 'Smart Filtering'],
        newRule: 'New Filing Rule',
        editRule: 'Edit Filing Rule',
        activeRules: 'Active Rules',
        filesFiled: 'Files Filed',
        todayFilings: 'Filed Today',
        cloudConnections: 'Cloud Connections',
        tabConnections: 'Cloud Connections',
        tabRules: 'Filing Rules',
        tabHistory: 'History',
        googleDriveDesc: 'File attachments directly to Google Drive. The system will create folders automatically based on AI settings.',
        dropboxDesc: 'File attachments directly to Dropbox. The system will create folders automatically based on AI settings.',
        onedriveDesc: 'File attachments directly to Microsoft OneDrive. The system will create folders automatically based on AI settings.',
        notConnected: 'Not connected',
        disconnect: 'Disconnect',
        connectGoogleDrive: 'Connect Google Drive',
        connectDropbox: 'Connect Dropbox',
        connectOneDrive: 'Connect OneDrive',
        connectedSuccess: 'Connected successfully!',
        connectionError: 'Connection error:',
        connectFirst: 'Connect a Google Drive, Dropbox, or OneDrive account to start filing documents automatically.',
        noRules: 'No Filing Rules',
        noRulesSubtitle: 'Create your first filing rule to start filing documents automatically from email.',
        connectCloudFirst: 'First connect a cloud account (Google Drive, Dropbox, or OneDrive) in the "Cloud Connections" tab',
        allEmails: 'All emails with files',
        runsToday: 'Runs today',
        noHistory: 'No filing history',
        noHistorySubtitle: 'Once filing rules are active, you\'ll see history here.',
        filterByRule: 'Filter by rule',
        filterBySender: 'Filter by sender',
        allRules: 'All rules',
        applyFilters: 'Filter',
        clearFilters: 'Clear filters',
        loadMore: 'Load more',
        showingXofY: 'Showing {shown} of {total}',
        rule: 'Rule',
        sender: 'Sender',
        filesCount: 'Files',
        disconnectConfirm: 'Are you sure? Filing rules using this connection will stop working.',
        connectionRemoved: 'Connection removed',
        errorDisconnect: 'Error removing connection',
        errorConnection: 'Connection error',
        ruleUpdated: 'Rule updated',
        ruleCreated: 'Rule created successfully',
        ruleDeleted: 'Rule deleted',
        deleteRuleConfirm: 'Delete this filing rule?',
        errorToggle: 'Error changing status',
        manualRunStarted: 'Manual run started',
        errorManualRun: 'Error starting manual run',
        testResult: 'Test Result',
        aiClassified: 'AI classified the document successfully (confidence: {confidence}%)',
        emailLabel: 'Email:',
        fileLabel: 'File:',
        docType: 'Document type:',
        filingPath: 'Filing path:',
        filePath: 'Path',
        fileLink: 'Link',
        fileName: 'File name:',
        destination: 'Destination:',
        stepSource: 'Source & Filters',
        stepAi: 'AI Instructions',
        stepDestination: 'Destination & Limits',
        ruleName: 'Rule Name',
        imapFolder: 'IMAP Folder',
        imapDefault: 'Default: INBOX',
        filterOptional: 'Filters (optional)',
        filterFrom: 'From (sender)',
        filterFromHelper: 'Email address, part of it, or comma-separated. Leave empty = all senders',
        filterSubject: 'Subject contains',
        filterSubjectHelper: 'Keywords in subject, comma-separated',
        filterBody: 'Email body contains',
        filterAttachment: 'Attachment name contains',
        filterAttachmentHelper: 'e.g.: "invoice", "order", "contract"',
        blockedSenders: 'Blocked senders',
        blockedSendersHelper: 'Email addresses or domains to block, comma-separated. Example: spam@example.com, noreply@domain.com',
        aiInstructions: 'Describe how you want documents to be filed. If left empty, AI will use the default method: Year > Sender Name > Document Type (invoice/order/contract/general).',
        filingPrompt: 'Filing Instructions (Prompt) — optional',
        filingPromptPlaceholder: 'Examples:\n\n• "Organize into folders by: Year > Client Name > Document Type (invoices, orders, contracts)"\n\n• "File everything in a single folder named \'Incoming Documents\' with date as prefix"\n\n• "Supplier invoices in \'Expenses > Year > Month\', customer invoices in \'Revenue > Year\'"\n\n• "PDF files containing \'invoice\' - Invoices folder. Excel files - Reports folder. Rest - General folder"',
        aiEngineOptional: 'AI Engine (optional)',
        modelOptional: 'Model (optional)',
        modelHelper: 'Leave empty to use the default model',
        cloudTarget: 'Cloud Target',
        targetFolder: 'Target Folder:',
        rootFolder: 'Root Folder',
        resetToRoot: 'Reset to root folder',
        browseFolder: 'Browse / Create New Folder',
        limitsLabel: 'Limits',
        maxRunsDay: 'Max runs per day',
        startHour: 'Start Hour',
        endHour: 'End Hour',
        activeDays: 'Active days:',
        enableAfterCreate: 'Enable rule immediately after creation',
        selectFolder: 'Select Target Folder',
        noSubfolders: 'No subfolders here',
        noFolders: 'No folders',
        createOrSelect: 'Create a new folder or select the current one',
        newFolderName: 'New folder name',
        createFolder: 'Create',
        folderCreated: 'Folder created successfully',
        errorCreateFolder: 'Error creating folder',
        selectThisFolder: 'Select This Folder',
        selectCloudFirst: 'Select a cloud connection first',
        nameRequired: 'Rule name is required',
        accountRequired: 'Email account is required',
        missingRuleName: 'Missing rule name (step 1)',
        missingAccount: 'Missing email account (step 1)',
        missingCloud: 'Missing cloud target',
        createRule: 'Create Rule',
        fromPrefix: 'From:',
        subjectPrefix: 'Subject:',
        filePrefix: 'File:',
        daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    },

    // ==================== INBOX (Webmail) ====================
    inbox: {
        compose: 'Compose',
        reply: 'Reply',
        replyAll: 'Reply All',
        forward: 'Forward',
        forwardAttachmentsFailed: 'attachment(s) could not be loaded for forwarding — try again or attach them manually',
        to: 'To',
        cc: 'CC',
        bcc: 'BCC',
        subject: 'Subject',
        noSubject: '(No subject)',
        send: 'Send',
        sending: 'Sending...',
        emailSent: 'Email sent successfully!',
        readReceipt: 'Read Receipt',
        openTracking: 'Open Tracking',
        priority: 'Priority',
        priorityHigh: 'High Priority',
        priorityNormal: 'Normal',
        priorityLow: 'Low Priority',
        encryption: 'Encryption',
        schedule: 'Schedule',
        templates: 'Templates',
        addAttachment: 'Add File',
        attachments: 'Attachments',
        dragFiles: 'Drag files here or click "Add File"',
        snooze: 'Reminder / Snooze',
        snooze1h: 'In 1 hour',
        snooze3h: 'In 3 hours',
        snoozeTomorrow: 'Tomorrow morning',
        snooze2d: 'In 2 days',
        snooze1w: 'In 1 week',
        addStar: 'Star',
        removeStar: 'Unstar',
        selected: 'selected',
        deleteSelected: 'Delete Selected',
        moveToArchive: 'Move to Archive',
        markRead: 'Mark as Read',
        markUnread: 'Mark as Unread',
        moveToFolder: 'Move to Folder',
        deselectAll: 'Deselect All',
        connecting: 'Connecting to mailbox...',
        noResults: 'No results found',
        emptyFolder: 'Folder is empty',
        inbox: 'Inbox',
        sentMail: 'Sent',
        drafts: 'Drafts',
        spam: 'Spam',
        trash: 'Trash',
        archive: 'Archive',
        flagged: 'Starred',
        allMail: 'All Mail',
        primary: 'Primary',
        social: 'Social',
        promotions: 'Promotions',
        updates: 'Updates',
        quickReplyPlaceholder: 'Quick reply...',
        sendQuickReply: 'Send',
        quickReplySent: 'Quick reply sent!',
        composePlaceholder: 'Write your message here...',
        addressPlaceholder: 'Enter address...',
        searchPlaceholder: 'Search emails...',
        aiAssist: 'AI Writing Assistant',
        aiCompose: 'Compose with AI',
        aiTones: {
            professional: 'Professional',
            friendly: 'Friendly',
            brief: 'Brief',
            formal: 'Formal',
            thankful: 'Grateful',
            decline: 'Polite Decline',
        },
        aiTonesDesc: {
            professional: 'Professional and polite response',
            friendly: 'Warm and personal',
            brief: 'Straight to the point, 3-4 sentences',
            formal: 'Like a business letter',
            thankful: 'Expressing appreciation',
            decline: 'Gentle decline with an alternative',
        },
        errorSendingEmail: 'Error sending email',
        errorLoadingMessage: 'Error loading message',
        deleteConfirm: 'Delete these messages?',
        newFolder: 'New Folder',
        renameFolder: 'Rename',
        deleteFolder: 'Delete Folder',
        folderName: 'Folder Name',
        wrote: 'wrote',
        forwardedMessage: 'Forwarded Message',
        fromLabel: 'From:',
        dateLabel: 'Date:',
        subjectLabel: 'Subject:',
        printEmail: 'Print',
        downloadEmail: 'Download',
        unifiedInbox: 'Unified Inbox',
        newMail: 'New mail',
        newMailBody: 'You have {count} new messages',
        connectionError: 'Error connecting to mailbox',
        connectionCooldown: 'Connection on temporary cooldown due to previous failures. Trying to reset...',
        resetDone: 'Reset done - reconnecting...',
        reconnectingAccount: 'The mail server temporarily rejected the connection - retrying...',
        cannotAutoReset: 'Cannot auto-reset - contact admin',
        mailboxUnavailable: 'Mailbox {email} is currently unavailable, switching to another...',
        errorLoadingMessages: 'Error loading messages',
        errorLoadingUnified: 'Error loading unified mailbox',
        errorAiReply: 'Error generating AI reply',
        errorAiCompose: 'Error with AI compose',
        errorEncryption: 'Encryption error',
        sendWithoutEncryption: 'Send without encryption?',
        errorSendingPrefix: 'Error sending: ',
        chooseDateTime: 'Please choose date and time',
        emailScheduled: 'Email scheduled successfully!',
        errorSchedulingPrefix: 'Error scheduling: ',
        errorCreatingFolderPrefix: 'Error creating folder: ',
        errorDeletingFolderPrefix: 'Error deleting folder: ',
        errorExportPrefix: 'Error exporting: ',
        confirmDeleteSearch: 'Delete all {count} messages matching search "{query}"?',
        errorDeletingSearchPrefix: 'Error deleting all search results: ',
        errorDeletingPrefix: 'Error deleting: ',
        confirmEmptyFolder: 'Empty "{folder}"? All messages will be permanently deleted and cannot be recovered.',
        errorEmptyingFolderPrefix: 'Error emptying folder: ',
        confirmMoveSearch: 'Move all {count} messages matching search "{query}" to folder?',
        errorMovingSearchPrefix: 'Error moving all search results: ',
        errorMovingPrefix: 'Error moving: ',
        confirmSpamSearch: 'Report all {count} matching messages as spam?',
        errorSpamSearchPrefix: 'Error reporting all search results as spam: ',
        confirmMoveToInbox: 'Move all {count} matching messages to inbox?',
        errorMoveToInboxPrefix: 'Error moving all search results to inbox: ',
        archiveNotFound: 'Archive folder not found',
        toLabel: 'To:',
        ccLabel: 'CC:',
        noName: 'No name',
        lastMsg: 'Latest',
        firstMsg: 'First',
        noText: '(No text)',
        noContent: '(No content)',
        noMessageContent: 'No content for this message.',
        pgpPassphrasePrompt: 'Enter encryption passphrase:',
        noEncryptedContent: 'No encrypted content found',
        decryptedContent: 'Decrypted content:',
        errorDecryption: 'Decryption error',
        decrypt: 'Decrypt',
        pgpEncrypted: 'This message is PGP encrypted',
        readReceiptSent: 'Read receipt sent',
        errorSendingReadReceipt: 'Error sending read receipt',
        sendConfirmation: 'Send confirmation',
        senderRequestedReadReceipt: 'The sender requested a read receipt for this message',
        autoReplied: 'Auto-replied',
        autoRepliedByAI: 'Auto-replied by AI',
        aiReplyTooltip: 'Reply with AI - automatic response',
        aiReplyLabel: 'AI Reply',
        errorArchive: 'Error moving to archive',
        errorDelete: 'Error deleting',
        notSpamTooltip: 'Not spam - move to inbox',
        reportSpamTooltip: 'Report as spam',
        createCalendarEvent: 'Create calendar event',
        openNewWindow: 'Open in new window',
        exportEml: 'Export as .eml',
        aiCategorize: 'AI Auto-categorize',
        classifyResult: 'Category',
        errorClassify: 'Error in auto-categorization',
        labelsTitle: 'Labels',
        noLabelsHint: 'No labels - create in settings',
        closePreview: 'Close preview',
        snoozeSet: 'Reminder set',
        errorSnooze: 'Error setting reminder',
        emailFrom: 'Email from:',
        eventCreated: 'Event created successfully in calendar!',
        errorCreatingEvent: 'Error creating event',
        addAccount: '+ Add Account',
        demo: 'Demo',
        emptyAction: 'Empty',
        welcomeTitle: 'Welcome to your Mailbox',
        welcomeSubtitle: 'Connect your email account to start receiving and sending messages. You can connect Gmail, Outlook, or any IMAP account.',
        connectMailbox: 'Connect Mailbox',
        connectHelpGuide: 'Gmail connection guide',
        connectHelpWhatsapp: 'Help on WhatsApp',
        connectBannerTitle: 'You haven’t connected a mailbox yet',
        connectBannerBody: 'Without a connected mailbox you can’t read email or use AI. It takes about a minute.',
        connectBannerCta: 'Connect mailbox now',
        connectBannerDismiss: 'Later',
        tryAgain: 'Try again',
        unread: 'Unread',
        attachmentsFilter: 'Files',
        advancedSearch: 'Advanced search',
        dateFrom: 'From date',
        dateTo: 'To date',
        searchInBody: 'Search in body',
        clearFilters: 'Clear',
        paginationOf: 'of',
        firstPage: 'First page',
        nextPage: 'Next page',
        prevPage: 'Previous page',
        lastPage: 'Last page',
        show: 'Show:',
        autoRefresh: 'Auto-refresh',
        off: 'Off',
        seconds30: '30 seconds',
        minute1: '1 minute',
        minutes2: '2 minutes',
        minutes5: '5 minutes',
        createEventFromEmail: 'Create calendar event from email',
        eventTitle: 'Title',
        eventDescription: 'Description',
        eventLocation: 'Location',
        eventStart: 'Start',
        eventEnd: 'End',
        createEventBtn: 'Create Event',
        addMailAccount: 'Add Email Account',
        connectToMailbox: 'Connect to Mailbox',
        loginPrompt: 'Enter your email address and password. Credentials will be saved so you don\'t need to enter them again.',
        emailAddress: 'Email Address',
        passwordLabel: 'Password',
        useAppPassword: 'Use an App Password (see guide below)',
        protocol: 'Protocol',
        localStorage: 'Local storage',
        pop3Warning: 'Suitable for older servers. Limitations: INBOX only, no read status sync, no server-side search.',
        localCacheInfo: 'Messages will be saved locally for fast loading and offline access.',
        addAndConnect: 'Add & Connect',
        connect: 'Connect',
        connectGmail: 'Connect Gmail',
        connectOutlook: 'Connect Outlook / Hotmail',
        connectYahoo: 'Connect Yahoo Mail',
        connectWorkspace: 'Connect Google Workspace / IMAP Account',
        openAppPasswords: 'Open app password setup ↗',
        copySetupLink: 'Copy setup link',
        gmailAppPasswordInfo: 'Gmail requires an App Password instead of your regular password.',
        gmailStep1: 'Go to myaccount.google.com/security',
        gmailStep2: 'Enable 2-Step Verification if not active',
        gmailStep3: 'Search for "App Passwords"',
        gmailStep4: 'Create a new app password for "Mail" → Copy the 16-character code',
        gmailStep5: 'Paste the code in the Password field above (no spaces)',
        outlookAppPasswordInfo: 'Outlook/Hotmail with 2FA requires an App Password for IMAP.',
        outlookStep1: 'Enable two-step verification on your Microsoft account if needed',
        outlookStep2: 'Go to account.live.com/proofs/AppPassword',
        outlookStep3: 'Create a new app password and copy it',
        outlookStep4: 'Paste it above and connect via IMAP',
        yahooAppPasswordInfo: 'Yahoo requires an app password for third-party IMAP access.',
        yahooStep1: 'Open Yahoo security settings and enable 2FA',
        yahooStep2: 'Generate an App Password for mail',
        yahooStep3: 'Paste the password here and connect',
        workspaceInfo: 'For Google Workspace (business domain) or other IMAP accounts:',
        workspaceStep1: '1. Make sure IMAP is enabled in account settings',
        workspaceStep2: '2. If 2-step verification is on, create an App Password',
        workspaceStep3: '3. Enter the full email address and app password',
        demoMode: 'Demo mode',
        demoHint: 'Demo mailbox loads instantly — no app password or provider setup.',
        autoFill: 'Auto-fill',
        sendFrom: 'Send from',
        sendAs: 'Send as',
        aiComposeDesc: 'Describe what you want to write, and AI will prepare a ready-to-send email with a subject line.',
        aiComposePlaceholder: 'Describe the email you want to send...',
        aiComposing: 'Composing...',
        aiComposeBtn: 'Compose email',
        aiGenerating: 'AI generating response...',
        aiGenerateReply: 'Generate reply with AI',
        chooseTone: 'Choose reply tone',
        aiExample1: 'Draft an employment agreement for our new employee',
        aiExample2: 'Write a meeting summary email based on the following points:',
        aiExample3: 'Summarize the following article for a newsletter:',
        aiExample4: 'Write a professional email for a business partnership',
        aiExample5: 'Draft a price quote for a client requesting consulting services',
        aiExample6: 'Write a polite response to a customer complaint',
        aiExample7: 'Draft an invitation to a conference / company event',
        aiExample8: 'Write a follow-up email after a business meeting',
        trackingStats: 'Tracking statistics',
        trackingStatsTitle: 'Email Tracking Statistics',
        noTrackingData: 'No tracking data yet. Send an email with "Open Tracking" enabled to see statistics.',
        sentOn: 'Sent',
        openedOn: 'Opened',
        openedTimes: 'Opened {count} times',
        pgpTooltip: 'PGP Encryption - email will be encrypted with recipient\'s public key',
        createFolder: 'Create Folder',
        deleteFolderWarning: 'This will delete the folder',
        deleteFolderConfirm: 'Are you sure you want to delete this folder? This action cannot be undone.',
        confirmDeleteFolder: 'Yes, delete folder',
        deleteMailAccount: 'Delete Email Account',
        confirmDeleteAccount: 'Are you sure you want to delete the account',
        deleteAccountWarning: 'This will remove the account from all your devices. You can add it back at any time.',
        confirmDelete: 'Confirm Delete',
        scheduleEmail: 'Schedule Email',
        scheduleDateLabel: 'Date and time to send',
        scheduling: 'Scheduling...',
        scheduleSend: 'Schedule Send',
        undoSendMsg: 'Email will be sent in 5 seconds...',
        undoSend: 'Undo Send',
        allSearchSelected: 'All {count} messages matching search "{query}" are selected.',
        cancelSelectAll: 'Cancel select all',
        allPageSelected: 'All {count} messages on this page are selected.',
        selectAllSearchResults: 'Select all {count} matching messages',
        onboardingTitle: 'Welcome to Email Lion!',
        onboardingStep1: 'Welcome',
        onboardingStep2: 'Folders',
        onboardingStep3: 'Compose',
        onboardingStep4: 'Tips',
        onboarding1Title: 'Your email system is ready!',
        onboarding1Desc: 'Email Lion is an advanced email system with professional tools to manage your emails. Let\'s explore the main features.',
        onboarding2Title: 'Folders & Organization',
        onboarding2Desc: 'On the side you\'ll find your mail folders. You can create new folders, drag emails between folders, and filter by smart categories.',
        onboarding3Title: 'Compose Email',
        onboarding3Desc: 'Click "Compose" or press C on your keyboard. You can attach files, schedule sends, use templates, and track email opens.',
        onboarding4Title: 'Pro Tips',
        onboarding4Desc: '• Press ? to view keyboard shortcuts\n• Press / to focus on search\n• Use quick filters to sort emails\n• Set up a personal signature in settings\n• Enable dark mode in the top menu',
        skip: 'Skip',
        letsStart: 'Let\'s start!',
        keyboardShortcuts: 'Keyboard Shortcuts',
        kbCompose: 'Compose new email',
        kbReply: 'Reply to email',
        kbForward: 'Forward email',
        kbDelete: 'Delete selected',
        kbEscape: 'Close / Deselect',
        kbSelectAll: 'Select all',
        kbSearch: 'Focus search',
        kbHelp: 'Help - Shortcuts',
        dragToReorder: 'Drag to reorder',
        defaultAccount: 'Default',
        dragReorderHint: 'Drag to reorder • First = default',
        cannotConnectMailServer: 'Cannot connect to the mail server. Check that the email address is correct.',
        cannotTestConnection: 'Cannot test the connection.',
        connectionSaveFailed: 'The connection was tested, but saving the mailbox failed. Please try again.',
        testingConnection: 'Testing connection...',
        loadingOriginalAttachments: 'Loading {count} original attachment(s)...',
        onDatePrefix: 'On ',
    },

    // ==================== SETTINGS ====================
    settings: {
        title: 'Settings',
        tabGeneral: 'General',
        tabAi: 'AI Agent',
        tabSecurity: 'Security',
        tabContacts: 'Contacts',
        tabSignatures: 'Signatures',
        tabTemplates: 'Templates',
        tabRules: 'Rules',
        tabLabels: 'Labels',
        tabAlias: 'Alias',
        tabVacation: 'Auto Reply',
        tabSendPermissions: 'Send Permissions',
        tabReferral: 'Referrals',
        viewMode: 'View Mode',
        language: 'Language',
        theme: 'Theme',
        notifications: 'Notifications',
        changePassword: 'Change Password',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        passwordChanged: 'Password changed successfully',
        twoFactor: 'Two-Factor Authentication (2FA)',
        enable2FA: 'Enable 2FA',
        disable2FA: 'Disable 2FA',
        pgpEncryption: 'PGP Encryption',
        addContact: 'Add Contact',
        editContact: 'Edit Contact',
        deleteContact: 'Delete Contact',
        contactName: 'Name',
        contactEmail: 'Email',
        contactPhone: 'Phone',
        contactCompany: 'Company',
        noContacts: 'No contacts',
        addSignature: 'Add Signature',
        editSignature: 'Edit Signature',
        deleteSignature: 'Delete Signature',
        defaultSignature: 'Default Signature',
        signatureName: 'Signature Name',
        signatureContent: 'Signature Content',
        noSignatures: 'No signatures',
        addTemplate: 'Add Template',
        editTemplate: 'Edit Template',
        deleteTemplate: 'Delete Template',
        templateName: 'Template Name',
        templateSubject: 'Subject',
        templateBody: 'Content',
        noTemplates: 'No templates',
        addRule: 'Add Rule',
        editRule: 'Edit Rule',
        deleteRule: 'Delete Rule',
        noRules: 'No rules',
        ruleName: 'Rule Name',
        ruleCondition: 'Condition',
        ruleAction: 'Action',
        addLabel: 'Add Label',
        editLabel: 'Edit Label',
        deleteLabel: 'Delete Label',
        noLabels: 'No labels',
        labelName: 'Label Name',
        labelColor: 'Color',
        vacationReply: 'Auto Reply (Vacation)',
        vacationEnabled: 'Enabled',
        vacationSubject: 'Reply Subject',
        vacationMessage: 'Reply Message',
        vacationStart: 'Start Date',
        vacationEnd: 'End Date',
        saved: 'Saved successfully',
        errorSaving: 'Error saving',
        referralTitle: 'Referral Program',
        referralCode: 'Your Referral Code',
        referralLink: 'Referral Link',
        copyLink: 'Copy Link',
        linkCopied: 'Link copied!',
        tabAdvanced: 'Advanced',
        referralInvitePrefix: 'Invite friends to Email Lion and get ',
        referralFreeMonth: 'a free month',
        referralInviteSuffix: ' for every paying friend!',
        referralYourCode: 'Your referral code',
        copied: 'Copied!',
        copyCode: 'Copy code',
        referralsCount: 'Referrals',
        rewardsCount: 'Rewards',
        aiConfigSaved: 'AI settings saved successfully',
        aiConfigError: 'Error saving AI settings',
        viewSaved: 'View saved',
        newSignature: 'New Signature',
        signatureSaved: 'Signature saved',
        signatureSaveError: 'Error saving signature',
        signatureDeleted: 'Signature deleted',
        signatureDeleteError: 'Error deleting signature',
        categoryGeneral: 'General',
        templateNameRequired: 'Template name required',
        templateSaved: 'Template saved',
        templateSaveError: 'Error saving template',
        templateDeleted: 'Template deleted',
        templateDeleteError: 'Error deleting template',
        emailRequired: 'Email required',
        contactSaved: 'Contact saved',
        contactSaveError: 'Error saving contact',
        contactDeleteError: 'Error deleting contact',
        passwordsMismatch: 'Passwords do not match',
        passwordMinLength: 'Password must be at least 6 characters',
        passwordChangeError: 'Error changing password',
        twoFASetupError: 'Error setting up 2FA',
        twoFAEnabled: '2FA enabled successfully!',
        twoFAVerifyError: 'Invalid verification code',
        twoFADisabled: '2FA disabled',
        twoFADisableError: 'Error disabling 2FA',
        pgpRequiredFields: 'Name, email and passphrase required',
        pgpKeyCreated: 'PGP key created successfully',
        pgpKeyCreateError: 'Error creating key',
        pgpPublicKeyImported: 'Public key imported',
        pgpImportError: 'Error importing key',
        pgpDeleteConfirm: 'Delete this key?',
        pgpKeyDeleted: 'Key deleted',
        deleteError: 'Error deleting',
        delegationAdded: 'Send permission added',
        delegationAddError: 'Error adding permission',
        delegationRemoved: 'Permission removed',
        delegationRemoveError: 'Error removing permission',
        defaultVacationSubject: 'I am currently unavailable',
        vacationActivated: 'Auto reply activated',
        vacationDeactivated: 'Auto reply deactivated',
        vacationSaveError: 'Error saving auto reply',
        ruleNameRequired: 'Rule name required',
        ruleSaved: 'Rule saved',
        ruleSaveError: 'Error saving rule',
        ruleDeleted: 'Rule deleted',
        ruleDeleteError: 'Error deleting rule',
        labelSaved: 'Label saved',
        labelSaveError: 'Error saving label',
        labelDeleted: 'Label deleted',
        labelDeleteError: 'Error deleting label',
        scheduledCancelled: 'Scheduled send cancelled',
        cancelError: 'Error cancelling',
        sessionTerminated: 'Session terminated',
        sessionTerminateError: 'Error disconnecting',
        allSessionsTerminated: 'All other sessions terminated',
        aliasEmailRequired: 'Alias email required',
        aliasUpdated: 'Alias updated',
        aliasAdded: 'Alias added',
        aliasDeleteConfirm: 'Delete this alias?',
        aliasDeleted: 'Alias deleted',
        mailboxShared: 'Mailbox shared successfully',
        revokeShareConfirm: 'Revoke sharing?',
        selectMailboxError: 'Please select a mailbox from the list. If none available, connect one from the Webmail page.',
        autoReplyAIEnabled: 'AI auto reply enabled!',
        autoReplyConfigSaved: 'Auto reply settings saved',
        docUploadedSuccess: 'uploaded successfully',
        uploadError: 'Error uploading',
        deleteDocConfirm: 'Delete this document from knowledge base?',
        docDeleted: 'Document deleted',
        contactExcluded: 'Contact excluded',
        exclusionRemoved: 'Exclusion removed',
        contactsImported: 'Contacts imported',
        contactsImportError: 'Error importing contacts',
        contactsImportedVcard: 'Contacts imported from vCard',
        vcardImportError: 'Error importing vCard',
        backupStarted: 'Backup download started...',
        backupCodesCopied: 'Backup codes copied',
        secretCopied: 'Secret copied',
        aiEngineTitle: 'AI Engine',
        aiEngineDesc: 'Choose your preferred AI engine for composing, replies, and auto reply.',
        defaultModelRecommended: 'Default model (recommended)',
        saveAiPreferences: 'Save AI Preferences',
        globalPromptTitle: 'System Prompt (Global)',
        globalPromptDesc: 'Set a global prompt for all AI operations: email composition, replies, and auto reply. Describe your business, tone, and special instructions.',
        globalPromptPlaceholder: 'Example: I am a business consultant, my tone is professional but warm. Always address by first name. End with \'Best regards, [my name]\'. Don\'t promise things I can\'t deliver.',
        composePromptTitle: 'Custom prompt for new email composition',
        composePromptPlaceholder: 'Additional instructions for composing new emails (empty = use global prompt)',
        replyPromptTitle: 'Custom prompt for AI replies',
        replyPromptPlaceholder: 'Additional instructions for email replies (empty = use global prompt)',
        autoReplyPromptTitle: 'Custom prompt for auto reply',
        autoReplyPromptPlaceholder: 'Additional instructions for auto reply (empty = use global prompt)',
        savePromptSettings: 'Save Prompt Settings',
        aiUsageAndBilling: 'AI Usage & Billing',
        runsPerMonth: 'runs per month',
        autoTopup: 'Auto Top-Up when runs are depleted',
        runsToAdd: 'Runs to add:',
        runs: 'runs',
        recentUsage: 'Recent usage (30 days):',
        dateColumn: 'Date',
        actionColumn: 'Action',
        providerColumn: 'Provider',
        quantityColumn: 'Quantity',
        featureCompose: 'Compose',
        featureReply: 'Reply',
        featureAutoReply: 'Auto Reply',
        storageQuotaTitle: 'Storage Quota',
        inUse: 'in use',
        allowed: 'allowed',
        storageUsedPercent: 'of storage used',
        accountsCount: 'accounts',
        contactsCountLabel: 'contacts',
        templatesCountLabel: 'templates',
        signaturesCountLabel: 'signatures',
        emailSignatures: 'Email Signatures',
        noSignaturesDesc: 'No signatures. Create a signature that will be added automatically to your messages.',
        defaultLabel: 'Default',
        mailHandlerTitle: 'Default Mail App',
        mailHandlerDesc: 'Make Email Lion the default handler for mailto: links across this device. Clicking an email address anywhere will open the compose dialog here.',
        mailHandlerButton: 'Set as default',
        mailHandlerInstalled: 'Registered with browser ✓',
        mailHandlerSuccess: 'Request sent to your browser. Approve the prompt above to enable.',
        mailHandlerError: 'Your browser rejected the request. It may already be approved, or the action requires HTTPS.',
        mailHandlerNotSupported: 'This browser does not expose the API for automatic mailto: protocol registration. If the app is already installed, the OS should offer it automatically. Otherwise, open the page in Chrome / Edge / Firefox.',
        mailHandlerHttpsRequired: 'Registering the handler requires a secure connection (HTTPS). Open the page at https://emaillion.net.',
        mailHandlerStandaloneTitle: 'App installed and running ✓',
        mailHandlerStandaloneDesc: 'Because the app is installed, your operating system controls how mailto: links are routed. To make it your default: click the test button below, or click any mailto: link OUTSIDE this app. The OS will show an app picker — choose Email Lion and mark "Always".',
        mailHandlerTestTitle: '🔗 Test',
        mailHandlerTestDesc: 'Clicking this button opens a sample mailto: link. If everything is wired up correctly, a compose window will open here in Email Lion. Otherwise the OS will show its app picker.',
        mailHandlerTestButton: 'Open a sample mailto: link',
        mailHandlerAndroidHint: 'Android: after installing the PWA, click any mailto: link in another app (browser, WhatsApp, etc.). Android shows "Open with" — pick Email Lion and tap "Always". To change later: System settings ➤ Apps ➤ Default apps ➤ Mail app.',
        mailHandlerIosHint: 'iOS: Apple does not allow PWAs to replace the default mail app. Use Email Lion via the browser or from your home screen.',
        mailHandlerDesktopHint: 'Desktop: clicking "Set as default" registers Email Lion with your browser. To set it as the OS-level default (Windows): Settings ➤ Apps ➤ Default apps ➤ Mail.',
        emailTemplates: 'Email Templates',
        newTemplate: 'New Template',
        noTemplatesDesc: 'No templates. Create templates for quick replies.',
        subjectPrefix: 'Subject:',
        noSubject: 'No subject',
        exportCsv: 'Export CSV',
        importCsv: 'Import CSV',
        exportVcard: 'Export vCard (.vcf) - Google / Apple / Outlook compatible',
        importVcard: 'Import vCard (.vcf) - from Google / Apple / Outlook',
        searchPlaceholder: 'Search...',
        addButton: 'Add',
        contactsSavedAuto: 'Contacts are saved automatically when sending emails. You can also add manually.',
        importExportContacts: 'Import/Export Contacts',
        csvDescription: 'Export and import in tabular format (Excel, Google Sheets)',
        vcardDescription: 'Standard contact format. Compatible with Google Contacts, Apple, Outlook',
        gmailMigrationHint: 'Moving from Gmail? Go to ',
        gmailMigrationHint2: ' > Export > Choose vCard > Import the file here',
        vacationActiveAlert: 'Auto reply active - anyone who emails you will receive an automatic response.',
        messageSubject: 'Message Subject',
        vacationPlaceholder: 'Hello, I am currently unavailable. I will get back to you soon...',
        fromDate: 'From date',
        toDate: 'Until date',
        sendToContactsOnly: 'Send only to my contacts',
        saveAndActivate: 'Save & Activate',
        saveDisabled: 'Save (disabled)',
        rulesAndFilters: 'Rules & Filters',
        rulesDescription: 'Rules allow you to sort, mark, or delete emails automatically based on conditions you set.',
        newRule: 'New Rule',
        noRulesDesc: 'No rules. Create a new rule to sort emails automatically.',
        ruleDisabled: 'Disabled',
        contactsManagement: 'Contacts Management',
        contactsManagementDesc: 'Manage, import, and export contacts. Select the "General" tab for full editing.',
        goToGeneralSettings: 'Go to general settings for contact management',
        minChars: 'Minimum 6 characters',
        confirmNewPassword: 'Confirm New Password',
        changePasswordBtn: 'Change Password',
        twoFactorDesc: 'Add an extra layer of security with an authenticator app like Google Authenticator.',
        twoFactorActive: '2FA is enabled and active',
        backupCodesTitle: 'Backup codes (save them in a safe place!):',
        copyAll: 'Copy All',
        disablePassword: 'Password to disable',
        disable2FABtn: 'Disable 2FA',
        scanQrCode: 'Scan the code in your authenticator app, or enter the secret manually.',
        scanWithAuthenticator: 'Scan with Google Authenticator',
        secretManualEntry: 'Secret (for manual entry)',
        enterCodeFromApp: 'Enter the code from the app',
        verifyAndEnable: 'Verify & Enable',
        enableTwoFactor: 'Enable Two-Factor Authentication',
        delegationTitle: 'Send Delegation',
        delegationDesc: 'Allow other users to send emails on your behalf. They can select your address as the sender when composing.',
        delegateEmailLabel: 'User email',
        sendAs: 'Send as',
        sendOnBehalf: 'Send on behalf',
        noDelegations: 'No active send permissions',
        pgpTitle: 'Email Encryption (PGP)',
        pgpDesc: 'Manage PGP keys for email encryption and digital signatures. Create new keys or import public keys of contacts.',
        createNewKey: 'Create New Key',
        pgpPassphrase: 'Encryption Passphrase',
        pgpPassphraseHelper: 'Remember this password - you\'ll need it for signing and decryption',
        createKey: 'Create Key',
        importPublicKey: 'Import public key:',
        importKey: 'Import Key',
        privateAndPublic: 'Private+Public',
        publicKey: 'Public',
        fingerprint: 'Fingerprint',
        noPgpKeys: 'No PGP keys configured',
        labelsTitle: 'Labels',
        addLabelBtn: 'Add Label',
        noLabelsAlert: 'No labels created yet. Labels allow you to categorize emails by color.',
        editLabelTitle: 'Edit Label',
        newLabelTitle: 'New Label',
        labelNameField: 'Label name',
        colorLabel: 'Color:',
        preview: 'Preview',
        scheduledEmails: 'Scheduled Emails',
        refreshBtn: 'Refresh',
        noScheduledEmails: 'No scheduled emails. You can schedule emails from the new message window.',
        recipient: 'Recipient',
        scheduledFor: 'Scheduled for',
        statusPending: 'Pending',
        statusSent: 'Sent',
        statusCancelled: 'Cancelled',
        statusFailed: 'Failed',
        cancelSend: 'Cancel send',
        noSubjectLabel: '(No subject)',
        aiAutoReplyTitle: 'AI Smart Auto Reply',
        active: 'Active',
        inactive: 'Inactive',
        selectMailbox: 'Select a mailbox to enable auto reply:',
        noMailboxes: 'No mailboxes connected. Connect a mailbox from the Webmail page to enable auto reply.',
        betaInfo: 'Auto reply will respond to incoming emails based on your prompt and knowledge base. Only real emails - not spam, ads, or newsletters.',
        activeMailbox: 'Active mailbox:',
        promptSetup: 'Prompt & Configuration',
        promptSetupDesc: 'Define who you are, your business, and how to respond. The more detailed, the better the responses.',
        toneLabel: 'Tone',
        toneProfessional: 'Professional',
        toneFriendly: 'Friendly',
        toneBrief: 'Brief',
        toneFormal: 'Formal',
        languageLabel: 'Language',
        hebrew: 'Hebrew',
        maxLengthWords: 'Max length (words)',
        delayMinutes: 'Delay (minutes)',
        knowledgeBase: 'Knowledge Base',
        documentsCount: 'documents',
        knowledgeBaseDesc: 'Upload documents for AI to use for accurate responses. Supported: TXT, CSV, JSON, HTML, MD.',
        uploadDocument: 'Upload Document',
        maxDocSize: 'Maximum 50KB per document',
        noDocuments: 'No documents. Upload documents for more accurate AI responses.',
        emailFiltering: 'Email Filtering',
        skipSpam: 'Skip spam',
        skipPromotions: 'Skip promotions',
        skipNewsletters: 'Skip newsletters',
        skipNoreply: 'Skip no-reply addresses',
        skipAutoReplies: 'Skip auto replies',
        onlyKnownContacts: 'Reply only to known contacts',
        startHour: 'Start hour',
        endHour: 'End hour',
        activeDays: 'Active days',
        maxRepliesPerDay: 'Max replies per day',
        maxRepliesPerSender: 'Max per sender',
        exclusionsTitle: 'Exclusions',
        exclusionsDesc: 'Contacts who will not receive auto replies - you will reply to them manually.',
        exclusionName: 'Name (optional)',
        exclusionReason: 'Reason',
        replyLog: 'Reply Log',
        sentCount: 'Sent',
        skippedCount: 'Skipped',
        failedCount: 'Failed',
        excludedCount: 'Excluded',
        avgTime: 'Avg time',
        noLogEntries: 'No entries yet.',
        senderColumn: 'Sender',
        reasonResponseColumn: 'Reason / Response',
        logStatusSent: 'Sent',
        logStatusSkipped: 'Skipped',
        logStatusExcluded: 'Excluded',
        logStatusFailed: 'Failed',
        saving: 'Saving...',
        saveAutoReplySettings: 'Save Auto Reply Settings',
        backupExport: 'Backup & Export Mailbox',
        backupDesc: 'Download all emails from the selected folder as a ZIP file. Each email is saved in .eml format.',
        inboxFolder: 'Inbox',
        sentFolder: 'Sent Mail',
        draftsFolder: 'Drafts',
        trashFolder: 'Trash',
        spamFolder: 'Spam',
        downloadBackup: 'Download Backup (ZIP)',
        aliasAddresses: 'Alias Addresses',
        addAlias: 'Add Alias',
        noAliases: 'No aliases configured. Add addresses to send emails from them.',
        noDisplayName: 'No display name',
        sharedMailboxes: 'Shared Mailboxes',
        shareMailbox: 'Share Mailbox',
        sharedWithMe: 'Shared with me:',
        myShares: 'My shares:',
        permissionRead: 'Read',
        permissionWrite: 'Write',
        permissionAdmin: 'Admin',
        sharedWith: 'Shared with:',
        permissionLabel: 'Permission:',
        noSharedMailboxes: 'No shared mailboxes. Click "Share Mailbox" to share with team members.',
        shareMailboxDialog: 'Share Mailbox',
        shareMailboxDesc: 'Select the mailbox you want to share and the user to share with.',
        mailboxToShare: 'Mailbox to share',
        noConnectedMailboxes: 'No connected mailboxes',
        shareWithUser: 'Share with user',
        searchUserPlaceholder: 'Search user by name or email...',
        noUsersFound: 'No users found',
        loadingUsers: 'Loading users...',
        noName: 'No name',
        adminRole: 'Admin',
        userRole: 'User',
        permissionLevel: 'Permission level',
        readOnlyDesc: 'Read only - view messages',
        readWriteDesc: 'Read + Write - send messages',
        fullAdminDesc: 'Full admin - including settings',
        shareBtn: 'Share',
        activeSessions: 'Active Sessions',
        disconnectAll: 'Disconnect All',
        noSessions: 'No active sessions recorded.',
        deviceBrowser: 'Device / Browser',
        ipAddress: 'IP Address',
        lastActivity: 'Last Activity',
        createdColumn: 'Created',
        unknown: 'Unknown',
        disconnectSession: 'Disconnect session',
        editAliasTitle: 'Edit Alias',
        newAliasTitle: 'Add New Alias',
        aliasEmailField: 'Email Address',
        displayNameField: 'Display Name',
        displayNamePlaceholder: 'Name shown to recipients',
        setAsDefault: 'Set as default address',
        ruleIfPrefix: 'If',
        conditionFrom: 'From',
        conditionTo: 'To',
        conditionSubject: 'Subject',
        conditionHasAttachment: 'Has attachment',
        conditionContains: 'Contains',
        conditionEquals: 'Equals',
        conditionStartsWith: 'Starts with',
        conditionEndsWith: 'Ends with',
        conditionYes: 'Yes',
        actionMoveToFolder: 'Move to folder',
        actionMarkRead: 'Mark as read',
        actionStar: 'Star',
        actionDelete: 'Delete',
        actionForward: 'Forward to address',
        valuePlaceholder: 'Value...',
        folderNamePlaceholder: 'Folder name (e.g.: Spam, Archive)',
        forwardPlaceholder: 'Forward address',
        ruleActive: 'Rule active',
        conditionLabel: 'Condition',
        actionLabel: 'Action',
        sigDialogPlaceholder: 'Write your signature here...',
        templateContentPlaceholder: 'Write the template content...',
        category: 'Category',
        subjectOptional: 'Subject (optional)',
        notesLabel: 'Notes',
        newContactTitle: 'New Contact',
        popupView: 'Popup Window',
        popupViewDesc: 'Clicking an email opens a dialog.',
        pageView: 'Separate Page (Gmail)',
        pageViewDesc: 'Clicking an email opens a full page.',
        previewView: 'Preview Pane (Outlook)',
        previewViewDesc: 'Click shows a preview on the side.',
        recommended: 'Recommended',
        fast: 'Fast',
        dayNamesShort: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
        betaModePrefix: 'BETA mode:',
        webmailPage: 'Webmail',
        promptExample: 'Example:\nI am Yossi, CEO of Emilion. We are a web development and design company.\n\nPricing:\n- Landing page: $800-$2,200\n- Online store: $2,200-$4,000\n- App: from $5,500\n\nPlease answer customer questions professionally and pleasantly.',
        dangerZone: 'Danger Zone',
        deleteAccountTitle: 'Delete Account',
        deleteAccountDesc: 'Deleting your account is irreversible. All your data will be marked as deleted and you will be logged out.',
        deleteAccountBtn: 'Delete Account',
        deleteIrreversible: 'This action is completely irreversible. Your account will be marked as deleted and you will not be able to log in again.',
        deleteConfirmQuestion: 'Are you sure you want to delete your account?',
        deleteAfterInfo: 'After deletion you will be logged out automatically. The system administrator will be notified.',
        deleteTypeToConfirm: 'To confirm, type in the field below:',
        deleteTypeLabel: 'Type to confirm',
        deleteContinue: 'Continue',
        deleteInProgress: 'Deleting...',
        deleteConfirmBtn: 'Confirm Deletion',
        deleteAccountError: 'Error deleting account',
        connectionTestSubject: 'Email Lion connection test',
        connectionTestBody: 'If this window opened in Email Lion — the setup works.',
    },

    // ==================== DASHBOARD PAGE ====================
    dashboard: {
        title: 'Dashboard',
        greeting: 'Hello',
        dateLocale: 'en-US',
        organizations: 'Organizations',
        activeMailboxes: 'Active Mailboxes',
        domains: 'Domains',
        storageUsed: 'Storage Used',
        activityTrend: 'Activity – Last 7 Days',
        activityByType: 'Activity by Type (30 Days)',
        storageByOrg: 'Storage by Organization',
        usersByRole: 'Users by Role',
        mailboxStatus: 'Mailbox Status',
        recentActivity: 'Recent Activity',
        noData: 'No data',
        noDataYet: 'No data yet',
        noDataToShow: 'No data to display',
        system: 'System',
        storageUsedLabel: 'Used',
        storageQuotaLabel: 'Quota',
        connectedAccounts: 'connected',
        hostedMailboxes: 'hosted',
        totalMailboxes: 'of {{total}} total',
        verifiedDomains: 'verified',
        actionsLabel: 'Actions',
        roles: {
            super_admin: 'Super Admin',
            org_admin: 'Org Admin',
            multi_org_admin: 'Multi-Org Admin',
            user: 'User',
        },
        actions: {
            login: 'Login',
            send_email: 'Send Email',
            create_user: 'Create User',
            update_user: 'Update User',
            delete_user: 'Delete User',
            create_org: 'Create Organization',
            create_domain: 'Create Domain',
            create_mailbox: 'Create Mailbox',
        },
    },

    // ==================== MODULES / TRIAL ====================
    modules: {
        automations: {
            title: 'AI Email Automations',
            tagline: 'Analyze incoming emails, extract data from files, create tasks automatically.',
            bullets: [
                'AI summarizes, categorizes and replies to every relevant email',
                'Scheduled daily report includes tasks, emails and calendar alerts',
                'Extract attachments and send to Drive/Dropbox with smart filing',
            ],
            trialDescription: 'During the trial you can create one automation and run it up to 10 times.',
            gentleNudge: 'Start with one automation on a real email. Once you see results, upgrading unlocks more automations and continuous runs.',
            primaryCta: 'Upgrade to Full Automations',
            secondaryCta: 'Connect a Mailbox First',
        },
        ai_agent: {
            title: 'Personal AI Agent',
            tagline: 'Free-form chat with an AI that knows your mailbox, calendar and tasks.',
            bullets: [
                'Ask in natural language, get actions done (send, classify, archive)',
                'Connects to Green Invoice, finds invoices, summarizes emails',
                'Keeps personal memory and prompt templates you design yourself',
            ],
            trialDescription: 'During the trial you get 10 free conversations.',
            gentleNudge: 'Try asking about your emails, calendar or automations. No credits are charged during the trial; afterwards you continue with a credit package.',
            primaryCta: 'Buy Credits',
            secondaryCta: 'Connect a mailbox for better answers',
        },
        filing: {
            title: 'Smart Document Filing',
            tagline: 'Every document arriving by email files itself into the right folder in Drive or Dropbox.',
            bullets: [
                'AI detects document type (invoice / contract / receipt) and files it automatically',
                'Secure OAuth connection to Google Drive and Dropbox',
                'Custom filing rules — by sender, keywords, content',
            ],
            trialDescription: 'During the trial you can create one rule and file up to 10 documents.',
            gentleNudge: 'You can create a cloud connection and a first rule to try. After a few successful filings, the full plan unlocks continuous filing.',
            primaryCta: 'Upgrade to Full Filing',
            secondaryCta: 'Create Your First Rule',
        },
        proposals: {
            title: 'Proposal System',
            tagline: 'Professionally designed proposals, online approval and digital signature — even on mobile.',
            bullets: [
                'Fast creation from a template, automatic VAT and discount calculation',
                'The client approves and signs online — no more PDFs to return by email',
                'Built-in initial payment collection, including a receipt',
            ],
            trialDescription: 'During the trial you can create up to 10 proposals.',
            gentleNudge: 'Create a few real proposals and see how clients approve online. After the trial, continue with a plan or the proposals module.',
            primaryCta: 'Upgrade to Unlimited Proposals',
            secondaryCta: 'Create a Sample Proposal',
        },
        trialEnded: 'Trial ended — upgrade',
        trialMode: 'Trial mode',
        trialRemaining: 'Trial: {remaining}/{max} left',
        trialAlertText: 'in trial mode — you have {remaining} of {max} uses left.',
        upgradeBtn: 'Upgrade',
        moduleFallback: 'Module',
        viewOnlyMode: 'View-only mode',
        upgradeNow: 'Upgrade now for full access',
        getQuote: 'Get a Quote',
        capReachedTitle: 'You reached the trial limit',
        trialEndedTitle: 'The trial has ended',
        trialExhaustedMsg: 'You have used up the trial allowance for this module. Upgrade to get full access and unlock all capabilities.',
        trialAutomationCapMsg: 'In trial mode you can create only one automation. Upgrading unlocks unlimited automations.',
        trialRuleCapMsg: 'In trial mode you can create only one rule. Upgrading unlocks unlimited rules.',
        upgradeNowBtn: 'Upgrade Now',
    },

    // ==================== UPGRADE PROMPT ====================
    upgrade: {
        upgradePlan: 'Upgrade Plan',
        choosePlan: 'Choose a Plan',
        perMailbox: '/mailbox',
        mailboxCount: 'Number of mailboxes:',
        billingCycle: 'Billing Cycle',
        monthly: 'Monthly',
        yearly: 'Yearly',
        perMonthShort: '/mo',
        yearlySavings: '(save 20%)',
        platformFee: 'Platform fee',
        mailboxes: 'mailboxes',
        volumeDiscount: 'Volume discount ({percent}%)',
        vat: 'VAT (18%)',
        totalYearlyVat: 'Yearly total incl. VAT',
        totalMonthlyVat: 'Monthly total incl. VAT',
        perMonthOnly: '= ₪{amount} per month only (incl. VAT)',
        payNow: 'Pay Now',
        paymentTitle: 'Payment',
        preparingPayment: 'Preparing payment page...',
        paymentFailed: 'Payment failed. Please try again.',
        noPaymentLink: 'No payment link received',
        paymentError: 'Error creating payment',
        freePlanBanner: 'You are on the free plan — upgrade to a paid plan for more mailboxes, storage and full modules',
        nearLimitBanner: 'You have reached {percent}% of your quota — upgrade to avoid being blocked',
        upgradeNow: 'Upgrade Now',
        getQuote: 'Get a Quote',
        getDetailedQuote: 'Get a Detailed Quote',
        upgradeToPaid: 'Upgrade to a Paid Plan',
        nearPlanLimit: 'Close to your plan limit',
        mailboxesUsage: 'Mailboxes: {used}/{max} ({percent}%)',
        storageUsage: 'Storage: {used} GB / {max} GB ({percent}%)',
    },

    // ==================== ATTACHMENTS ====================
    attachments: {
        attachedFiles: 'Attachments',
        file: 'File',
        clickToView: 'Click to view',
        clickToDownload: 'Click to download',
        download: 'Download',
        downloadError: 'Error downloading file',
        loadError: 'Error loading file',
        cannotLoadFile: 'Cannot load the file',
        tryDownload: 'Try downloading',
        downloadFile: 'Download the file',
        downloadFileShort: 'Download file',
        loadingFile: 'Loading file...',
        loadingEditView: 'Loading edit view...',
        loading: 'Loading...',
        pdfRenderError: 'Cannot load PDF preview. You can draw on the white background — signatures will be saved to the file.',
        pdfNotSupported: 'Your browser does not support embedded PDF viewing',
        ofTotal: 'of',
        finishEditing: 'Finish editing',
        editAnnotate: 'Edit / Annotate',
        signature: 'Signature',
        saveSignedPdf: 'Save signed PDF',
        print: 'Print',
        replyWithFile: 'Reply with file',
        drawTool: 'Drawing tool',
        draw: 'Draw',
        addText: 'Add text',
        text: 'Text',
        handTool: 'Hand tool (drag signature)',
        move: 'Move',
        undoLast: 'Undo last',
        clearAll: 'Clear all',
        eSignature: 'Electronic Signature',
        drawSignatureTab: 'Draw Signature',
        savedSignaturesTab: 'Saved Signatures',
        uploadImageTab: 'Upload Image',
        drawSignatureHint: 'Draw your signature in the field below. It will be saved as a transparent PNG.',
        clear: 'Clear',
        signatureNamePlaceholder: 'Signature name (optional)',
        saveSignatureTooltip: 'Save signature for future use',
        save: 'Save',
        noSavedSignatures: 'No saved signatures',
        drawNewSignature: 'Draw a new signature',
        signatureDefault: 'Signature',
        setAsDefault: 'Set as default',
        deleteSig: 'Delete',
        uploadSignatureImage: 'Upload a signature image',
        uploadHint: 'Transparent PNG recommended. Other images (JPG, WEBP) will be converted automatically.',
        chooseImage: 'Choose Image',
        imageSavedToAccount: 'The image will be saved to your account for reuse',
        cancel: 'Cancel',
        placeSignatureOnDoc: 'Place signature on document',
        clickSigToPlace: 'Click a signature to place it on the document',
        drawSignatureFirst: 'Please draw a signature first',
        signatureSaved: 'Signature saved successfully',
        signatureSaveError: 'Error saving signature',
        chooseImageFile: 'Please choose an image file',
        imageSavedAsSignature: 'Image saved as signature',
        imageSaveError: 'Error saving image',
        importedSignature: 'Imported signature',
        signatureDeleted: 'Signature deleted',
        signatureDeleteError: 'Error deleting signature',
        signedFileSaved: 'Signed file saved successfully',
        signedFileSaveError: 'Error saving annotated PDF',
        signedFileAttached: 'Signed file attached to reply',
        fileAttached: 'File attached to reply',
        attachError: 'Error attaching file',
        noSubject: '(no subject)',
        emailFrom: 'From:',
        emailTo: 'To:',
        emailCc: 'Cc:',
        emailDate: 'Date:',
        emptyEmailBody: 'This attached email is empty.',
        nestedAttachments: 'Files inside this email',
        csvPreviewHint: 'Showing {shown} of {total} rows',
        noCalendarEvents: 'No events found in this calendar file',
        untitledEvent: 'Untitled event',
        eventWhen: 'When',
        eventWhere: 'Where',
        noContacts: 'No contacts found',
        unnamedContact: 'Unnamed contact',
        officeNeedsDownload: 'Word / Excel / PowerPoint files open after download, like in most mail apps.',
        msgNeedsDownload: 'Outlook .msg files cannot be opened in the browser. Download it, or ask the sender for an .eml.',
    },

    // ==================== AI AGENT ====================
    aiAgent: {
        title: 'Personal AI Agent',
        subtitle: 'Ask questions and perform tasks on emails, calendar, automations and filing. Sensitive actions await approval.',
        subtitleShort: 'Ask questions and perform tasks on emails, calendar, automations and filing.',
        conversations: 'Conversations',
        noConversations: 'No conversations yet',
        newConversation: 'New conversation',
        deleteConversationAria: 'Delete conversation',
        deleteConversationConfirm: 'Delete this conversation from history?',
        conversationDeleted: 'Conversation deleted from history',
        errorLoadConversations: 'Error loading agent conversations',
        errorLoadConversation: 'Error loading conversation',
        errorDeleteConversation: 'Error deleting conversation',
        errorSendMessage: 'Error sending message to agent',
        errorLoadGreenInvoice: 'Error loading Green Invoice settings',
        errorLoadMemories: 'Error loading agent memories',
        errorLoadPending: 'Error loading pending actions',
        errorLoadUsage: 'Error loading usage & cost reports',
        errorLoadBilling: 'Error loading billing settings',
        greenInvoiceSaved: 'Green Invoice settings saved successfully',
        errorSaveGreenInvoice: 'Error saving Green Invoice settings',
        connectionTestFailed: 'Connection test failed',
        memorySaved: 'Memory saved successfully',
        errorSaveMemory: 'Error saving memory',
        memoryDeleted: 'Memory deleted',
        errorDeleteMemory: 'Error deleting memory',
        billingSaved: 'Billing settings saved',
        errorSaveBilling: 'Error saving billing settings',
        errorCreatePayment: 'Error creating payment for credit package',
        paymentsSynced: 'Checked payments and updated the credit balance if payment was completed',
        errorSyncPayments: 'Error syncing credit payment',
        topupEnabled: 'Automatic top-up enabled. Opening an initial payment to fill the balance now.',
        errorSaveTopup: 'Error saving top-up settings',
        templateNeedsNameAndText: 'Please give the template a name and text',
        templateUpdated: 'Template updated',
        templateSaved: 'Template saved',
        errorSaveTemplate: 'Error saving template',
        deleteTemplateConfirm: 'Delete this template?',
        errorDeleteTemplate: 'Error deleting template',
        errorConfirmation: 'Error handling confirmation',
        doneFallback: 'Done.',
        actionApproved: 'The action was approved and executed.',
        actionCancelled: 'The action was cancelled and not executed.',
        templatesBtn: 'Templates',
        pendingActionsBtn: 'Pending Actions',
        pendingActionsTooltip: 'Pending actions ({count})',
        settingsBtn: 'Settings',
        personalScopeChip: 'Personal scope only',
        approvalChip: 'Approval before write actions',
        availableCredits: 'Available credits: {amount}',
        greenInvoiceConnectedChip: 'Green Invoice connected',
        questionTemplates: 'Question templates',
        howCanIHelp: 'How can I help?',
        examplesText: 'For example: "Find emails from client Danny", "Analyze my calendar this week", "Fully sync my Google calendar", "Create an automation for supplier invoices" or "Organize my Drive files".',
        pendingApprovalTitle: 'Actions awaiting approval',
        pendingApprovalText: 'There are {count} actions from all agent conversations waiting for your approval.',
        openApprovalBoard: 'Open Approval Board',
        toolResult: 'Tool result:',
        purchase: 'Buy',
        enableTopup: 'Enable Top-up',
        actionApprovalRequired: 'Action approval required',
        toolLabel: 'Tool:',
        approveAndRun: 'Approve & Run',
        cancelBtn: 'Cancel',
        agentThinking: 'The agent is thinking...',
        inputPlaceholder: 'Tell the agent what you want to do...',
        templatesDialogTitle: 'Agent question templates',
        newTemplate: 'New Template',
        templatesDialogHint: 'Choose a template to copy its text into the prompt field. You can also create reusable personal templates.',
        editTemplate: 'Edit Template',
        category: 'Category',
        templateName: 'Template name',
        templateText: 'Template text',
        updateTemplate: 'Update Template',
        saveTemplate: 'Save Template',
        myTemplates: 'My Templates',
        noCustomTemplates: 'You have not created personal templates yet. Click "New Template" to add your first one.',
        editTemplateAria: 'Edit template',
        deleteTemplateAria: 'Delete template',
        builtinTemplates: 'Built-in Templates',
        customCategory: 'Personal',
        settingsTitle: 'AI Agent Settings',
        tabMemories: 'Memories',
        tabApprovals: 'Actions to Approve',
        tabUsage: 'Usage & Billing',
        tabInvoices: 'Invoices',
        invoicesTitle: 'Invoices',
        invoicesHint: 'Enter API credentials so the agent can search documents and reports in Green Invoice. The key and secret are stored encrypted.',
        saveScope: 'Save scope',
        scopeMyUserOnly: 'My user only',
        scopeOrg: 'Entire organization',
        existingConnection: 'Existing connection: key {key} · secret {secret}',
        savedLabel: 'Saved',
        enterApiKey: 'Enter API Key',
        enterApiSecret: 'Enter API Secret',
        keepEmptyKey: 'Leave empty to keep the existing key.',
        keepEmptySecret: 'Leave empty to keep the existing secret.',
        testConnection: 'Test Connection',
        memoriesTitle: 'Manage Personal Memories',
        memoryType: 'Type',
        memoryKey: 'Key',
        memoryValue: 'Memory value',
        updateMemory: 'Update Memory',
        addMemory: 'Add Memory',
        cancelEdit: 'Cancel Edit',
        noMemories: 'No saved memories yet.',
        updatedAtLabel: 'Updated',
        noPendingActions: 'No actions awaiting approval right now.',
        conversationLabel: 'Conversation:',
        usageTitle: 'AI Credits, Usage & Costs',
        creditBalance: 'Credit balance',
        availableOf: 'Available {available} of {total}',
        lowBalance: 'Low balance',
        refreshCredits: 'Refresh Credits',
        syncPayment: 'Sync Payment',
        reportScope: 'Report scope',
        scopeMyUser: 'My user',
        refreshReport: 'Refresh Report',
        requestsLabel: 'Requests',
        tokensLabel: 'Tokens',
        costLabel: 'Cost',
        recommendedCharge: 'Recommended charge',
        expectedProfit: 'Expected profit',
        billingSettingsTitle: 'Billing & Profit Settings',
        billingScope: 'Billing scope',
        markupPercent: 'Profit markup (%)',
        saveMarkup: 'Save Markup',
        markupHint: 'The recommended charge is calculated from the actual AI cost plus the configured profit markup.',
        buyCreditsTitle: 'Buy AI Agent Credits',
        syncPaymentAndUpdate: 'Sync payment & update balance',
        promptRoles: ['Management', 'Finance', 'Customer Service', 'Sales'],
        promptTemplates: [
            { role: 'Management', label: 'Daily Summary', prompt: 'Summarize what happened today in my emails, calendar and automations, and suggest the next 5 actions by urgency.' },
            { role: 'Management', label: 'Status Report', prompt: 'Produce a short report on this week\'s work status: clients awaiting replies, upcoming meetings, and automations that need review.' },
            { role: 'Management', label: 'Capability Check', prompt: 'Check which capabilities are currently available to me: emails, calendar, automations, filing and cloud connections.' },
            { role: 'Management', label: 'Task Prep', prompt: 'Prepare a follow-up task list from my recent emails, without performing any action before my approval.' },
            { role: 'Management', label: 'Process Improvement', prompt: 'Review my automations list and suggest 3 practical improvements to reduce manual work.' },
            { role: 'Finance', label: 'Invoices in Email', prompt: 'Search my emails for messages about "invoice" and summarize the 10 most important results.' },
            { role: 'Finance', label: 'Green Invoice', prompt: 'Search Green Invoice for documents related to client "Danny" and summarize document number, date, status and amount.' },
            { role: 'Finance', label: 'Open Documents', prompt: 'Search Green Invoice for open or unpaid documents and order them by urgency.' },
            { role: 'Finance', label: 'Financial Report', prompt: 'Produce a short report from Green Invoice on last month\'s documents: how many were found, which clients stand out and what needs attention.' },
            { role: 'Finance', label: 'Invoice Automation', prompt: 'Create a new automation that detects emails about invoices, extracts amount, vendor and date, and sends the result to my email. Ask for approval before creating.' },
            { role: 'Customer Service', label: 'Client Search', prompt: 'Search for emails related to a client named "Danny" and show me subject, sender and date for each result.' },
            { role: 'Customer Service', label: 'Handling Summary', prompt: 'Search for emails about "price quote" and summarize what needs attention and what is already closed.' },
            { role: 'Customer Service', label: 'Draft Reply', prompt: 'Draft a professional email to a client confirming we received the documents and will get back with an answer by tomorrow. Do not send without my approval.' },
            { role: 'Customer Service', label: 'Weekly Calendar', prompt: 'Analyze and summarize my calendar events for the next seven days by day and time, including alerts/reminders and any conflicts.' },
            { role: 'Customer Service', label: 'Sync Calendar', prompt: 'Run a full sync of my Google/Outlook calendar connections, then briefly summarize what changed.' },
            { role: 'Customer Service', label: 'Create Event', prompt: 'Create a calendar event for tomorrow at 10:00 named "Client follow-up call" lasting one hour, with a 15-minute reminder. Show confirmation before executing.' },
            { role: 'Sales', label: 'Hot Leads', prompt: 'Find emails from the last week that hint at a lead or a quote request, and order them by likelihood to close.' },
            { role: 'Sales', label: 'Follow-up Email', prompt: 'Prepare a short email to a client requesting the missing details for a quote. Show me the recipient, subject and content before approving to send.' },
            { role: 'Sales', label: 'Email Accounts', prompt: 'Show me all email accounts connected to my user, including display name and protocol.' },
            { role: 'Sales', label: 'Cloud Connections', prompt: 'Show me my active Google Drive and Dropbox connections and the filing rules configured for them.' },
            { role: 'Sales', label: 'Personal Memory', prompt: 'Remember that I prefer receiving summaries in English, with action items at the top of the list and no unnecessary detail.' },
        ],
    },

    // ==================== PWA INSTALL BANNER ====================
    pwa: {
        installSubtitle: 'Install the app for a faster experience',
        iosHowTo: 'To install on iPhone:',
        iosStep1: '1. Tap the Share button',
        iosStep2: '2. Scroll down and choose "Add to Home Screen"',
        iosStep3: '3. Tap "Add" in the top corner',
        gotIt: 'Got it',
        installInstructions: 'Install instructions',
        installApp: 'Install app',
        notNow: 'Not now',
        noStoreNeeded: 'No store download needed • Quick access from your home screen',
    },

    // ==================== PAYMENT RESULT ====================
    payment: {
        successTitle: 'Payment Completed | Email Lion',
        failureTitle: 'Payment Failed | Email Lion',
        successHeading: 'Payment completed successfully!',
        successBody: 'Thank you for your purchase. Your plan will be activated shortly and you will receive an email with activation details.',
        loginBtn: 'Log in',
        homeBtn: 'Home page',
        failureHeading: 'Payment was not completed',
        failureBody: 'An error occurred while processing the payment. You can try again or contact support.',
        tryAgain: 'Try again',
    },
};

export default en;
